import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { finishSocialPulseLogin } from "@/lib/socialpulse";

// Handles the OIDC redirect back from SocialPulse: completes the PKCE exchange,
// creates our app session, then returns the user to where they started.
export default function SocialPulseCallback() {
  const nav = useNavigate();
  const { oidcSession } = useAuth();
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { id_token, nonce } = await finishSocialPulseLogin();
        const res = await oidcSession(id_token, nonce);
        toast.success(res?.is_new_user ? "Welcome to IndiaAlumni!" : "Welcome back!");
        let dest = "/dashboard";
        try {
          const n = sessionStorage.getItem("sp_next");
          if (n && n.startsWith("/") && !n.startsWith("//")) dest = n;
        } catch { /* ignore */ }
        if (res?.is_new_user) {
          // First-time SocialPulse member -> onboarding (which returns to sp_next after).
          nav("/onboarding", { replace: true });
        } else {
          try { sessionStorage.removeItem("sp_next"); } catch { /* ignore */ }
          nav(dest, { replace: true });
        }
      } catch (e) {
        setError(e.message || "SocialPulse sign-in failed.");
      }
    })();
  }, [nav, oidcSession]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-5 py-24 text-center" data-testid="socialpulse-callback">
      {!error ? (
        <>
          <span className="grid h-12 w-12 animate-pulse place-items-center rounded-2xl bg-[#6d28d9]/10 text-[#6d28d9]"><Sparkle size={24} weight="fill" /></span>
          <p className="mt-4 font-heading text-lg font-medium">Completing your SocialPulse sign-in…</p>
        </>
      ) : (
        <>
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-destructive/10 text-destructive"><WarningCircle size={24} weight="fill" /></span>
          <p className="mt-4 font-heading text-lg font-medium">Sign-in didn't complete</p>
          <p className="mt-1 text-sm text-destructive" data-testid="socialpulse-callback-error">{error}</p>
          <button onClick={() => nav("/login", { replace: true })} className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground" data-testid="socialpulse-callback-retry">Back to sign in</button>
        </>
      )}
    </div>
  );
}
